import { Reward } from '../types';

// Message id, not display text — resolving it here would need an `intl`, which a
// module-level constant cannot have. `TimelineItem` translates it at render time.
const GIFT_LABEL = 'TIMELINE.REWARD.GIFT';

/**
 * Deliveries that earn a Surprise & Delight gift, keyed by delivery cycle in
 * days. The numbers are delivery counts (the `n` in the customer's `FOC-n` tag),
 * not days.
 *
 * Every milestone is a gift. The programme used to mix in percentage discounts
 * at a few stops (and a discount+gift combo on the first delivery); it no longer
 * does, which is why these are plain number lists rather than reward objects.
 *
 * The schedules are time-anchored, not count-anchored: milestones 3 to 5 land at
 * roughly the same elapsed time on every cycle (~4, ~7 and ~9-10 months), so a
 * 7-day plan runs all the way to delivery 36 while a 56-day plan finishes at 6.
 * That is why the lists are hand-written per cycle instead of derived — the
 * first two stops are fixed at deliveries 1 and 3 for everyone, and only the
 * last three scale with the interval.
 */
export const REWARD_SCHEDULES: Record<number, number[]> = {
  7: [1, 3, 12, 24, 36],
  14: [1, 3, 9, 16, 22],
  21: [1, 3, 7, 11, 15],
  28: [1, 3, 5, 9, 12],
  35: [1, 3, 5, 7, 9],
  42: [1, 3, 4, 6, 8],
  56: [1, 3, 4, 5, 6],
};

/**
 * Used when the cycle is unknown or still loading. Matches the fallback baked
 * into `getSubscriptionCycleSelector`, which also lands on 14 when it cannot
 * parse a cycle out of the line SKU.
 */
export const DEFAULT_REWARD_CYCLE = 14;

const SUPPORTED_CYCLES = Object.keys(REWARD_SCHEDULES)
  .map(Number)
  .sort((a, b) => a - b);

/**
 * Maps any cycle onto one we have a schedule for.
 *
 * Selling plans are created in Shopify, so a cycle can appear in a SKU before it
 * appears here. Rather than render an empty timeline, fall back to the nearest
 * cycle we do know, breaking ties toward the LONGER one — a longer cycle has
 * fewer milestones, so we under-promise rather than advertise gifts the
 * programme will not actually grant.
 */
export const resolveRewardCycle = (cycle?: number | string | null): number => {
  const parsed = typeof cycle === 'string' ? parseInt(cycle, 10) : cycle;

  if (parsed == null || !Number.isFinite(parsed)) return DEFAULT_REWARD_CYCLE;
  if (REWARD_SCHEDULES[parsed]) return parsed;

  return SUPPORTED_CYCLES.reduce((closest, candidate) => {
    const closestDelta = Math.abs(closest - parsed);
    const candidateDelta = Math.abs(candidate - parsed);

    if (candidateDelta < closestDelta) return candidate;
    if (candidateDelta === closestDelta) return Math.max(closest, candidate);

    return closest;
  }, SUPPORTED_CYCLES[0]);
};

/** The timeline's stops for a given cycle, in delivery order. */
export const getRewardsForCycle = (cycle?: number | string | null): Reward[] =>
  REWARD_SCHEDULES[resolveRewardCycle(cycle)].map((order) => ({ order, label: GIFT_LABEL }));

/**
 * The last delivery that earns a gift on this cycle. The dashboard uses it to
 * stop showing the timeline once a customer has collected everything.
 */
export const getMaxRewardOrder = (cycle?: number | string | null): number => {
  const schedule = REWARD_SCHEDULES[resolveRewardCycle(cycle)];

  return schedule[schedule.length - 1];
};

/**
 * How many stops the narrow (sub-`md`) layout shows at once. Wider layouts render
 * the whole schedule, so there is only one number to tune here.
 */
export const MOBILE_VISIBLE_ITEMS = 3;
